import {
  type DragEvent,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { FolderTree, Movable } from './folder-tree'

/** How long a drag rests on a shut folder before it opens, as in Finder. */
const SPRING_OPEN_MS = 600
const DRAG_TYPE = 'application/x-studio-document'

/*
 * What is being dragged, held outside React: the sidebar is drawn by the shell
 * and the cards by the page, and one drag crosses from either into the other.
 */
let dragging: Movable | null = null
const listeners = new Set<() => void>()
function setDragging(item: Movable | null) {
  dragging = item
  for (const listen of listeners) listen()
}
function subscribe(listen: () => void) {
  listeners.add(listen)
  return () => {
    listeners.delete(listen)
  }
}
const useDragging = () => useSyncExternalStore(subscribe, () => dragging)

/**
 * Props that let a card be picked up.
 *
 * Dragging is the pointer's way in only. The same move is always offered by
 * keyboard through "Move to folder", so nothing here is the only way to file.
 */
export function useDragSource(item: Movable) {
  return {
    draggable: true,
    onDragStart: (event: DragEvent<HTMLElement>) => {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(DRAG_TYPE, `${item.kind}:${item.id}`)
      setDragging(item)
    },
    onDragEnd: () => setDragging(null),
  }
}

/**
 * Props that make an element a place to drop, and whether an allowed drag is
 * over it now. `accepts` narrows a target that is no folder at all: Unfiled
 * takes documents, the top level takes folders. `onSpringOpen` fires when a drag
 * rests here whether or not it may land, so a document can pass through its own
 * folder into one inside it.
 */
export function useDropTarget({
  target,
  tree,
  move,
  accepts = 'any',
  onSpringOpen,
}: {
  target: string | null
  tree: FolderTree
  move: (item: Movable, target: string | null) => void
  accepts?: 'any' | 'doc' | 'folder'
  onSpringOpen?: () => void
}) {
  const held = useDragging()
  const [over, setOver] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clear = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }
  /* A target that goes away mid-hover — its folder just opened — fires
     nothing afterwards. */
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  /* A drag that ended anywhere leaves no target lit. */
  if (!held && over) setOver(false)

  const kindFits =
    !!held &&
    (accepts === 'any' || (accepts === 'folder') === (held.kind === 'folder'))
  const allowed = !!held && kindFits && tree.canLand(held, target)

  return {
    isOver: over && allowed,
    props: {
      onDragEnter: () => {
        if (!held) return
        setOver(true)
        if (onSpringOpen && !timer.current)
          timer.current = setTimeout(onSpringOpen, SPRING_OPEN_MS)
      },
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (!allowed) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
      },
      onDragLeave: (event: DragEvent<HTMLElement>) => {
        const next = event.relatedTarget
        if (next instanceof Node && event.currentTarget.contains(next)) return
        setOver(false)
        clear()
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault()
        setOver(false)
        clear()
        setDragging(null)
        if (held && allowed) move(held, target)
      },
    },
  }
}
